import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight, Download, RotateCcw, Save, Upload } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { inputCls } from "./EaUi";
import { MONTHS_IT, fmtQty } from "./eaFormat";
import { NAME, downloadBlob, errMsg, num, priceDigits, str } from "./eaFixUtil";

const btn = "h-9 px-3 rounded-lg border border-slate-200 bg-white text-sm inline-flex items-center gap-2 disabled:opacity-50";
const avg = (xs) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);

function PointSummary({ rows, bands, unit, open, onToggle, id }) {
  const prev = rows.filter((r) => r.status === "FORECAST");
  const pct = avg(rows.map((r) => r.pct_fixed).filter((x) => x != null));
  const prices = rows.flatMap((r) => bands.map((b) => r.prices[b])).filter((x) => x != null);
  const Icon = open ? ChevronDown : ChevronRight;
  return (
    <tr className="border-t border-slate-200 bg-white hover:bg-slate-50 cursor-pointer" onClick={onToggle} data-testid={`ea-fix-point-${id}`}>
      <td className="px-3 py-2.5 font-semibold text-slate-900" colSpan={2}><span className="inline-flex items-center gap-1.5"><Icon size={15} />{id}</span></td>
      <td className="px-3 py-2.5 text-right font-mono-nums">{pct != null ? `${fmtQty(pct, 1)}%` : "—"} <span className="text-xs text-slate-400">media</span></td>
      <td className="px-3 py-2.5 text-right font-mono-nums" colSpan={bands.length}>{prices.length ? `${fmtQty(avg(prices), priceDigits(unit))} ${unit}` : "—"} <span className="text-xs text-slate-400">· {prev.length} mesi prev.{rows.some((r) => r.edited) ? " · modificato" : ""}</span></td>
    </tr>
  );
}

export function EaFixings({ commodity, year, params, unit, unitSel, onSaved, v }) {
  const [d, setD] = useState(null);
  const [ed, setEd] = useState({});
  const [openPts, setOpenPts] = useState({});
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const q = { ...params, commodity, year, unit };
  const load = () => api.get("/ea/budget/fixings", { params: q }).then((r) => { setD(r.data); setEd({}); });
  useEffect(() => { load(); }, [commodity, year, unit, params.company_id, v]); // eslint-disable-line react-hooks/exhaustive-deps
  const groups = useMemo(() => Object.entries((d?.rows || []).reduce((a, r) => ({ ...a, [r.identifier]: [...(a[r.identifier] || []), r] }), {})), [d]);
  if (!d) return null;
  const k = (r) => `${r.code}|${r.month}`;
  const dg = priceDigits(d.unit);
  const val = (r, f) => ed[k(r)]?.[f] ?? (f === "pct" ? str(r.pct_fixed) : str(r.prices[f], dg + 1));
  const set = (r, f, v) => setEd({ ...ed, [k(r)]: { ...ed[k(r)], [f]: v } });
  const done = (data, msg) => { setD(data); setEd({}); toast.success(msg); onSaved(); };
  const save = async () => {
    const items = d.rows.filter((r) => ed[k(r)]).map((r) => ({ code: r.code, month: r.month, pct_fixed: num(val(r, "pct")), prices: Object.fromEntries(d.bands.map((b) => [b, num(val(r, b))])) }));
    if (items.some((i) => Number.isNaN(i.pct_fixed) || Object.values(i.prices).some(Number.isNaN))) return toast.error("Valori non validi");
    try { const r = await api.put("/ea/budget/fixings", { commodity, year, unit, items }, { params }); done(r.data, "Fixing salvati"); } catch (e) { toast.error(errMsg(e, "Salvataggio non riuscito")); }
  };
  const reset = async () => { const r = await api.delete("/ea/budget/fixings", { params: { ...params, commodity, year } }); setD(null); load(); toast.success("Ripristinati i fixing dell'Excel"); onSaved(); return r; };
  const exportXlsx = () => downloadBlob("/ea/budget/fixings.xlsx", q, `fixing_${commodity.toLowerCase()}_${year}.xlsx`).catch(() => toast.error("Export non riuscito"));
  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try { const r = await api.post("/ea/budget/fixings/import", fd, { params: q }); await load(); toast.success(`Importate ${r.data.imported_rows} righe modificate`); onSaved(); }
    catch (err) { toast.error(errMsg(err, "Importazione non riuscita")); }
    setBusy(false);
  };
  return (
    <div className="card-elevated overflow-x-auto" data-testid={`ea-fixings-${commodity}`}>
      <div className="p-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-display font-semibold text-slate-900" data-pdf-title>Fixing e prezzi · {NAME[commodity]} {year}</div>
          <div className="text-xs text-slate-500">Clicca un {commodity === "POWER" ? "POD" : "PDR"} per vedere e modificare i mesi. Modificabili i mesi previsionali; righe evidenziate = modificate. Import Excel: usa il modello esportato.</div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {unitSel}
          <button onClick={exportXlsx} className={btn} data-testid={`ea-fix-export-${commodity}`}><Download size={14} /> Esporta Excel</button>
          <button onClick={() => ref.current?.click()} disabled={busy} className={btn} data-testid={`ea-fix-import-${commodity}`}><Upload size={14} /> {busy ? "Importazione…" : "Importa Excel"}</button>
          <input ref={ref} type="file" accept=".xlsx" className="hidden" onChange={upload} data-testid={`ea-fix-import-input-${commodity}`} />
          <button onClick={save} disabled={!Object.keys(ed).length} className="h-9 px-3 rounded-lg bg-slate-900 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50" data-testid={`ea-fix-save-${commodity}`}><Save size={15} /> Salva</button>
          <button onClick={reset} className={btn} data-testid={`ea-fix-reset-${commodity}`}><RotateCcw size={14} /> Ripristina da Excel</button>
        </div>
      </div>
      {!groups.length ? <div className="px-4 pb-4 text-sm text-slate-400">Nessun fixing nel file per l'anno: importa un Excel per inserirli.</div> : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500"><tr><th className="px-3 py-2 text-left">Punto</th><th className="px-3 py-2 text-left">Mese</th><th className="px-3 py-2 text-right">% fissata</th>{d.bands.map((b) => <th key={b} className="px-3 py-2 text-right">{b === "MONTH" ? "Prezzo" : `Prezzo ${b}`} {d.unit}</th>)}</tr></thead>
          <tbody>
            {groups.map(([id, rows]) => (
              <React.Fragment key={id}>
                <PointSummary id={id} rows={rows} bands={d.bands} unit={d.unit} open={openPts[id]} onToggle={() => setOpenPts({ ...openPts, [id]: !openPts[id] })} />
                {openPts[id] && rows.map((r) => (
                  <tr key={k(r)} className={`border-t border-slate-100 ${r.edited ? "bg-amber-50" : "bg-slate-50/40"} ${r.status === "FORECAST" ? "" : "text-slate-400"}`} data-testid={`ea-fix-row-${r.identifier}-${r.month}`}>
                    <td className="px-3 py-1.5 pl-9 text-xs text-slate-400">{r.identifier}</td><td className="px-3 py-1.5">{MONTHS_IT[r.month - 1]}{r.status === "FORECAST" ? " · prev." : ""}</td>
                    {["pct", ...d.bands].map((f) => <td key={f} className="px-2 py-1 text-right"><input value={val(r, f)} disabled={r.status !== "FORECAST"} onChange={(e) => set(r, f, e.target.value)} className={`${inputCls} w-24 text-right disabled:bg-slate-50 disabled:text-slate-400`} data-testid={`ea-fix-${f}-${r.identifier}-${r.month}`} /></td>)}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
